import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Cloud as RegistryIcon,
  Folder as RepoIcon,
  Label as TagIcon,
  CloudDownload as PullIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Dns as DnsIcon,
} from '@mui/icons-material';
import { ociRegistryApi, OCIRegistryConfig, OCIAssetLink } from '../services/oci-registry-api';
import { useJobs } from '../contexts/JobsContext';
import { Sync as SyncIcon, Link as LinkIcon } from '@mui/icons-material';

// ============================================================================
// Add/Edit Registry Dialog
// ============================================================================

interface RegistryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  registry?: OCIRegistryConfig | null;
}

const RegistryDialog: React.FC<RegistryDialogProps> = ({ open, onClose, onSave, registry }) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [authType, setAuthType] = useState<'anonymous' | 'basic' | 'token'>('anonymous');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (registry) {
        setName(registry.name);
        setUrl(registry.url);
        setAuthType(registry.authType);
        setUsername(registry.username || '');
        setPassword('');
        setToken('');
      } else {
        setName('');
        setUrl('');
        setAuthType('anonymous');
        setUsername('');
        setPassword('');
        setToken('');
      }
      setError(null);
    }
  }, [open, registry]);

  const handleSave = async () => {
    if (!name || !url) {
      setError('Name and URL are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const config = {
        name,
        url,
        authType,
        ...(authType === 'basic' && { username, password: password || undefined }),
        ...(authType === 'token' && { token: token || undefined }),
      };

      if (registry) {
        await ociRegistryApi.updateRegistry(registry.id, config);
      } else {
        await ociRegistryApi.addRegistry(config);
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{registry ? 'Edit Registry' : 'Add Registry'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g., Docker Hub, GHCR, Harbor"
          />
          <TextField
            label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g., https://registry-1.docker.io"
            helperText="Base URL of the OCI/Docker registry"
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Authentication</InputLabel>
            <Select value={authType} onChange={(e) => setAuthType(e.target.value as 'anonymous' | 'basic' | 'token')} label="Authentication">
              <MenuItem value="anonymous">Anonymous (public)</MenuItem>
              <MenuItem value="basic">Username / Password</MenuItem>
              <MenuItem value="token">Bearer Token</MenuItem>
            </Select>
          </FormControl>
          {authType === 'basic' && (
            <>
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Password / Access Token"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                size="small"
                fullWidth
                type="password"
                helperText={registry ? 'Leave blank to keep existing' : undefined}
              />
            </>
          )}
          {authType === 'token' && (
            <TextField
              label="Bearer Token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              size="small"
              fullWidth
              type="password"
              helperText={registry ? 'Leave blank to keep existing' : undefined}
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {registry ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Pull to S3 Dialog
// ============================================================================

interface PullDialogProps {
  open: boolean;
  onClose: () => void;
  registryId: string;
  repository: string;
  tag: string;
}

const PLATFORM_OPTIONS = [
  'linux/amd64',
  'linux/arm64',
  'linux/arm/v7',
  'linux/s390x',
  'linux/ppc64le',
];

const PullToS3Dialog: React.FC<PullDialogProps> = ({ open, onClose, registryId, repository, tag }) => {
  const [s3Key, setS3Key] = useState('');
  const [platform, setPlatform] = useState('linux/amd64');
  const [pulling, setPulling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Generate default S3 key
      const safeName = repository.replace(/\//g, '-');
      setS3Key(`assets/containers/${safeName}-${tag}.tgz`);
      setPlatform('linux/amd64');
      setError(null);
      setJobId(null);
    }
  }, [open, repository, tag]);

  const handlePull = async () => {
    if (!s3Key) {
      setError('S3 key is required');
      return;
    }

    setPulling(true);
    setError(null);

    try {
      const result = await ociRegistryApi.pullToS3(registryId, repository, tag, s3Key, platform);
      setJobId(result.jobId);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPulling(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pull Image to S3</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Pull <strong>{repository}:{tag}</strong> and save as a docker-save archive (.tgz) in S3.
          </Typography>
          <FormControl size="small" fullWidth disabled={!!jobId}>
            <InputLabel>Platform / Architecture</InputLabel>
            <Select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              label="Platform / Architecture"
            >
              {PLATFORM_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="S3 Destination Key"
            value={s3Key}
            onChange={(e) => setS3Key(e.target.value)}
            size="small"
            fullWidth
            disabled={!!jobId}
            helperText="The S3 key (path) where the image archive will be saved"
          />
          {jobId && (
            <Alert severity="success">
              Pull job started (ID: {jobId.substring(0, 8)}...). Track progress in the Jobs panel.
              A link will be automatically created between this S3 file and the source image.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{jobId ? 'Close' : 'Cancel'}</Button>
        {!jobId && (
          <Button onClick={handlePull} variant="contained" disabled={pulling} startIcon={<PullIcon />}>
            {pulling ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Pull to S3
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// Main Browser Component
// ============================================================================

export const OCIRegistryBrowser: React.FC = () => {
  const [registries, setRegistries] = useState<OCIRegistryConfig[]>([]);
  const [selectedRegistry, setSelectedRegistry] = useState<OCIRegistryConfig | null>(null);
  const [repositories, setRepositories] = useState<string[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repoFilter, setRepoFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [manualRepo, setManualRepo] = useState('');

  // Links
  const [tagLinks, setTagLinks] = useState<OCIAssetLink[]>([]);
  const [allLinks, setAllLinks] = useState<OCIAssetLink[]>([]);
  const [syncing, setSyncing] = useState<string | null>(null);

  // Dialogs
  const [registryDialogOpen, setRegistryDialogOpen] = useState(false);
  const [editingRegistry, setEditingRegistry] = useState<OCIRegistryConfig | null>(null);
  const [pullDialog, setPullDialog] = useState<{ registryId: string; repository: string; tag: string } | null>(null);

  // Load registries
  const loadRegistries = async () => {
    try {
      const regs = await ociRegistryApi.listRegistries();
      setRegistries(regs);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const loadAllLinks = async () => {
    try {
      const links = await ociRegistryApi.listLinks();
      setAllLinks(links);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadRegistries();
    loadAllLinks();
  }, []);

  // Load repositories when registry is selected
  const loadRepositories = async (registry: OCIRegistryConfig) => {
    setLoading(true);
    setError(null);
    setRepositories([]);
    setTags([]);
    setSelectedRepo(null);

    try {
      const repos = await ociRegistryApi.listRepositories(registry.id);
      setRepositories(repos);
      if (repos.length === 0) {
        setError('No repositories found. The registry may not support catalog listing. Use manual repository search below.');
      }
    } catch (err: any) {
      setError('Could not list repositories. Use manual repository search below.');
    } finally {
      setLoading(false);
    }
  };

  // Load tags for a repository
  const loadTags = async (repository: string) => {
    setLoading(true);
    setError(null);
    setTags([]);
    setTagLinks([]);
    setSelectedRepo(repository);

    try {
      const tagList = await ociRegistryApi.listTags(selectedRegistry!.id, repository);
      setTags(tagList);
      // Fetch links for this registry+repo
      try {
        const links = await ociRegistryApi.getLinksByImage(selectedRegistry!.id, repository);
        setTagLinks(links);
      } catch { /* ignore link fetch errors */ }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const getLinksForTag = (tagName: string): OCIAssetLink[] => {
    return tagLinks.filter(l => l.tag === tagName);
  };

  const handleSyncLink = async (link: OCIAssetLink) => {
    setSyncing(link.id);
    try {
      await ociRegistryApi.syncLink(link.id);
      // Refresh links
      if (selectedRegistry && selectedRepo) {
        const links = await ociRegistryApi.getLinksByImage(selectedRegistry.id, selectedRepo);
        setTagLinks(links);
      }
      await loadAllLinks();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSyncing(null);
    }
  };

  const handleUnlinkTag = async (link: OCIAssetLink) => {
    try {
      await ociRegistryApi.removeLink(link.id);
      setTagLinks(prev => prev.filter(l => l.id !== link.id));
      setAllLinks(prev => prev.filter(l => l.id !== link.id));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleSelectRegistry = (reg: OCIRegistryConfig) => {
    setSelectedRegistry(reg);
    setRepositories([]);
    setTags([]);
    setTagLinks([]);
    setSelectedRepo(null);
    setError(null);
  };

  const handleDeleteRegistry = async (id: string) => {
    if (!confirm('Delete this registry configuration?')) return;
    try {
      await ociRegistryApi.deleteRegistry(id);
      if (selectedRegistry?.id === id) {
        setSelectedRegistry(null);
        setRepositories([]);
        setTags([]);
      }
      await loadRegistries();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleManualRepoSearch = () => {
    if (!manualRepo.trim() || !selectedRegistry) return;
    loadTags(manualRepo.trim());
  };

  const filteredRepos = repositories.filter(r =>
    !repoFilter || r.toLowerCase().includes(repoFilter.toLowerCase())
  );
  const filteredTags = tags.filter(t =>
    !tagFilter || t.toLowerCase().includes(tagFilter.toLowerCase())
  );

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DnsIcon color="primary" />
        <Typography variant="h6" sx={{ flex: 1 }}>
          OCI Container Registries
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => { setEditingRegistry(null); setRegistryDialogOpen(true); }}
        >
          Add Registry
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flex: 1, overflow: 'hidden' }}>
        {/* Left panel: Registries + Repositories */}
        <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Registry list */}
          <Paper variant="outlined" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
              Registries
            </Typography>
            <List dense disablePadding>
              {registries.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No registries configured"
                    secondary="Click 'Add Registry' to get started"
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
              {registries.map((reg) => (
                <ListItemButton
                  key={reg.id}
                  selected={selectedRegistry?.id === reg.id}
                  onClick={() => handleSelectRegistry(reg)}
                  dense
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <RegistryIcon fontSize="small" color={selectedRegistry?.id === reg.id ? 'primary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={reg.name}
                    secondary={reg.url}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setEditingRegistry(reg); setRegistryDialogOpen(true); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleDeleteRegistry(reg.id); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemButton>
              ))}
            </List>
          </Paper>

          {/* Repository list */}
          {selectedRegistry && (
            <Paper variant="outlined" sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  Repositories
                </Typography>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => loadRepositories(selectedRegistry)} disabled={loading}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Manual repo search */}
              <Box sx={{ px: 1.5, pb: 1, display: 'flex', gap: 0.5 }}>
                <TextField
                  size="small"
                  placeholder="Enter repo name..."
                  value={manualRepo}
                  onChange={(e) => setManualRepo(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualRepoSearch()}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  }}
                  sx={{ '& .MuiInputBase-root': { height: 32 } }}
                />
                <Button size="small" variant="outlined" onClick={handleManualRepoSearch} disabled={!manualRepo.trim()}>
                  Go
                </Button>
              </Box>

              {repositories.length > 0 && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                  <TextField
                    size="small"
                    placeholder="Filter repos..."
                    value={repoFilter}
                    onChange={(e) => setRepoFilter(e.target.value)}
                    fullWidth
                    sx={{ '& .MuiInputBase-root': { height: 28, fontSize: '0.8rem' } }}
                  />
                </Box>
              )}

              <List dense disablePadding sx={{ overflow: 'auto', flex: 1 }}>
                {loading && !selectedRepo && (
                  <ListItem>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <ListItemText primary="Loading..." primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                )}
                {filteredRepos.map((repo) => (
                  <ListItemButton
                    key={repo}
                    selected={selectedRepo === repo}
                    onClick={() => loadTags(repo)}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <RepoIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={repo}
                      primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace', fontSize: '0.8rem' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        {/* Right panel: Tags */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {selectedRepo ? (
            <Paper variant="outlined" sx={{ height: '100%', overflow: 'auto' }}>
              <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1, fontFamily: 'monospace' }}>
                  {selectedRepo}
                </Typography>
                <Chip label={`${tags.length} tag(s)`} size="small" variant="outlined" />
                <Tooltip title="Refresh tags">
                  <IconButton size="small" onClick={() => loadTags(selectedRepo)} disabled={loading}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ px: 2, pb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Filter tags..."
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  }}
                  sx={{ '& .MuiInputBase-root': { height: 32 } }}
                />
              </Box>

              <Divider />

              {loading && selectedRepo && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!loading && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tag</TableCell>
                        <TableCell>Link</TableCell>
                        <TableCell sx={{ width: 220 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTags.map((tag) => {
                        const links = getLinksForTag(tag);
                        const hasLink = links.length > 0;
                        return (
                        <TableRow key={tag} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TagIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                                {tag}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {hasLink && links.map(link => (
                              <Tooltip key={link.id} title={`S3: ${link.s3Key} (${link.platform})`}>
                                <Chip
                                  icon={<LinkIcon />}
                                  label={link.s3Key.split('/').pop()}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  onDelete={() => handleUnlinkTag(link)}
                                  sx={{ maxWidth: 200, fontFamily: 'monospace', fontSize: '0.7rem' }}
                                />
                              </Tooltip>
                            ))}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              {hasLink && (
                                <Tooltip title="Sync (re-pull)">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={syncing === links[0].id ? <CircularProgress size={14} /> : <SyncIcon />}
                                    onClick={() => handleSyncLink(links[0])}
                                    disabled={syncing === links[0].id}
                                  >
                                    Sync
                                  </Button>
                                </Tooltip>
                              )}
                              <Tooltip title="Pull to S3">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PullIcon />}
                                  onClick={() => setPullDialog({
                                    registryId: selectedRegistry!.id,
                                    repository: selectedRepo,
                                    tag,
                                  })}
                                >
                                  Save to S3
                                </Button>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                      {filteredTags.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              {tagFilter ? 'No tags match filter' : 'No tags found'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                  Linked Images
                </Typography>
                <Chip label={`${allLinks.length} link(s)`} size="small" variant="outlined" />
                <Tooltip title="Refresh links">
                  <IconButton size="small" onClick={loadAllLinks}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider />
              {allLinks.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No linked images yet. Pull an image from a registry to auto-create a link.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Source Image</TableCell>
                        <TableCell>Platform</TableCell>
                        <TableCell>S3 Key</TableCell>
                        <TableCell>Last Synced</TableCell>
                        <TableCell sx={{ width: 140 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allLinks.map((link) => (
                        <TableRow key={link.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                                {link.repository}:{link.tag}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {link.registryName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={link.platform} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {link.s3Key}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                              {link.lastSynced ? new Date(link.lastSynced).toLocaleString() : 'Never'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Tooltip title="Sync (re-pull)">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleSyncLink(link)}
                                  disabled={syncing === link.id}
                                >
                                  {syncing === link.id ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Unlink">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleUnlinkTag(link)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Dialogs */}
      <RegistryDialog
        open={registryDialogOpen}
        onClose={() => setRegistryDialogOpen(false)}
        onSave={loadRegistries}
        registry={editingRegistry}
      />

      {pullDialog && (
        <PullToS3Dialog
          open={true}
          onClose={() => setPullDialog(null)}
          registryId={pullDialog.registryId}
          repository={pullDialog.repository}
          tag={pullDialog.tag}
        />
      )}
    </Box>
  );
};
